import { useState, useEffect, useRef } from 'react';
import { loadInitialSatellites, queryAllSatellites } from '../../utils/SatelliteRTree';
import { loadCDMs, getConjunctionsForSatellite, getAllSatelliteIdsWithConjunctions } from '../../utils/InitCdm';
import { CATEGORY_COLOR_MAP } from './SatelliteViewer';
import './SatelliteFilterControlDashboard.css';
import { useDebounce } from 'use-debounce';



const GROUPED_SATELLITE_CATEGORIES = [
  {
    group: 'Default',
    items: [
      { label: 'ISS (International Space Station)', value: 'iss' },
      { label: 'HST (Hubble Space Telescope)', value: 'hst' },
      // { label: 'JWST', value: 'jwst' },
      { label: 'Starlink', value: 'starlink' }
    ]
  },
  {
    group: 'Risk Monitoring',
    items: [
      { label: 'Active Conjunctions', value: 'conjunction' }
    ]
  },
  {
    group: 'Scientific & Observational',
    items: [
      { label: 'Science', value: 'science' },
      { label: 'Earth Observation', value: 'earth observation' },
      { label: 'Weather', value: 'weather' }
    ]
  },
  {
    group: 'Commercial & Technology',
    items: [
      { label: 'Communication', value: 'communication' },
      { label: 'Tech Demo', value: 'tech demo' },
      { label: 'Navigation', value: 'navigation' }
    ]
  },
  {
    group: 'Defense & Operations',
    items: [
      { label: 'Military', value: 'military' },
      { label: 'Space Station', value: 'space station' },
      { label: 'Debris', value: 'debris' }
    ]
  },
  {
    group: 'Uncategorized',
    items: [
      { label: 'Unknown', value: 'unknown' }
    ]
  }
];
// Orbit options
const ORBIT_OPTIONS = [
  { label: 'LEO', value: 'LEO' },
  { label: 'MEO', value: 'MEO' },
  { label: 'GEO', value: 'GEO' },
  { label: 'HEO', value: 'HEO' },
];

export const SatelliteFilterControls = ({ 
  onFilter, 
  onLoadComplete, 
  clusterCount, 
  satelliteName, 
  satelliteCountry, 
  launchDate,
  isTrackingSatellite,
  setIsTrackingSatellite,
  conjunctions = [],
  onConjunctionModeChange,
  // filterTrigger,
}) => {
  
  const conjunctionListRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const scrollStartRef = useRef(0);
  const wrapperRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState(['iss', 'starlink', 'hst']);
  const [selectedOrbits, setSelectedOrbits] = useState([]);

  const allCategories = GROUPED_SATELLITE_CATEGORIES.flatMap(group => group.items.map(item => item.value));
  const allOrbits = ORBIT_OPTIONS.map(o => o.value);

  // const [selectedCategories, setSelectedCategories] = useState(allCategories);
  // const [selectedOrbits, setSelectedOrbits] = useState(allOrbits);

  const [results, setResults] = useState([]);
  const [collapsed, setCollapsed] = useState(true);

  const isConjunctionModeActive = selectedCategories.includes('conjunction');

  // const [debouncedCategories] = useDebounce(selectedCategories, 200);
  // const [debouncedOrbits] = useDebounce(selectedOrbits, 200);
  const debouncedCategories = selectedCategories;
  const debouncedOrbits = selectedOrbits;

  const handleCheckboxChange = (category) => {
    setSelectedCategories(prevSelected => {
      let updated;

      if (prevSelected.includes(category)) {
        // Deselecting whatever was clicked
        updated = prevSelected.filter(item => item !== category);
      } else {
        if (category === 'conjunction') {
          // Selecting "Active Conjunctions" clears every other category
          updated = ['conjunction'];
        } else if (prevSelected.includes('conjunction')) {
          // Selecting any other category while "conjunction" is active
          // clears conjunction and starts fresh with just this one
          updated = [category];
        } else if (category === 'communication') {
          updated = [...prevSelected.filter(c => c !== 'starlink'), category];
        } else if (category === 'starlink') {
          updated = [...prevSelected.filter(c => c !== 'communication'), category];
        } else {
          updated = [...prevSelected, category];
        }
      }
      return updated;
    });
  };

  const handleOrbitChange = (orbit) => {
    const updated = selectedOrbits.includes(orbit)
      ? selectedOrbits.filter((item) => item !== orbit)
      : [...selectedOrbits, orbit];
    setSelectedOrbits(updated);
    // runFilter(selectedCategories, updated);
  };

  useEffect(() => {
    if (!loading) {
      runFilter(debouncedCategories, debouncedOrbits);
    }
  }, [debouncedCategories, debouncedOrbits, loading]);

  

  useEffect(() => {
    const init = async () => {
      await loadInitialSatellites();
      await loadCDMs();
      setLoading(false);
      onLoadComplete(true);
      setSelectedCategories(['iss', 'starlink', 'hst']);
      setSelectedOrbits([]);
      setIsTrackingSatellite(true);
    };
    init();
  }, [onLoadComplete]);

  useEffect(() => {
    onConjunctionModeChange?.(selectedCategories.includes('conjunction'));
  }, [selectedCategories, onConjunctionModeChange]);

  const toggleCollapse = () => {
    setCollapsed(prev => !prev);
  };

  useEffect(() => {
    if (collapsed) return;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [collapsed]);


  const runFilter = (categories, orbits) => {
    let filtered = [];

    const categorySet = new Set(categories.map((c) => c.toLowerCase()));

    categories.forEach((category) => {
      let result = [];

      if (category === 'iss') {
        result = queryAllSatellites({ name: 'iss', matchMode: 'startsWith' });
      } else if (category === 'starlink') {
        result = queryAllSatellites({ name: 'starlink', matchMode: 'startsWith' });
      } else if (category === 'hst') {
        result = queryAllSatellites({ name: 'hst', matchMode: 'includes' });
      } else if (category === 'conjunction') {
        // NEW — pull every active satellite currently involved in a conjunction
        const conjunctionIds = getAllSatelliteIdsWithConjunctions();
        result = queryAllSatellites({}).filter((sat) => conjunctionIds.has(String(sat.satelliteId)));
      } else if (category === 'space station') {
        result = queryAllSatellites({ category: 'space station' });
        if (categorySet.has('iss')) {
          result = result.filter((sat) => !sat.name.toLowerCase().startsWith('iss'));
        }
      } else if (category === 'communication') {
        result = queryAllSatellites({ category: 'communication' });
        if (categorySet.has('starlink')) {
          result = result.filter((sat) => !sat.name.toLowerCase().startsWith('starlink'));
        }
      } else {
        result = queryAllSatellites({ category });
      }

      filtered.push(...result);
    });

    // Optional fallback if categories are empty
    if (categories.length === 0 && orbits.length > 0) {
      filtered = queryAllSatellites({});
    }

    // Orbit filter
    if (orbits.length > 0) {
      filtered = filtered.filter((sat) => orbits.includes(sat.orbit_type));
    }

    // Deduplicate by satelliteId (crucial)
    const deduped = Array.from(
      new Map(filtered.map(sat => [sat.satelliteId, sat])).values()
    );

    // Sort satellites by satelliteId to stabilize clustering
    deduped.sort((a, b) => a.satelliteId.localeCompare(b.satelliteId));

    setResults(deduped);
    onFilter(deduped);
  };

  function getSeverity(pc, minRangeM) {
    if (pc != null) {
      if (pc >= 1e-3) return { level: 'critical', label: 'Critical' };
      if (pc >= 1e-4) return { level: 'severe', label: 'Severe' };
      if (pc >= 1e-5) return { level: 'moderate', label: 'Moderate' };
      return { level: 'mild', label: 'Mild' };
    }

    // No published Pc — fall back to miss distance as a rough proxy
    if (minRangeM == null) return { level: 'unknown', label: 'Unrated' };
    if (minRangeM < 500) return { level: 'critical', label: 'Critical' };
    if (minRangeM < 1000) return { level: 'severe', label: 'Severe' };
    if (minRangeM < 5000) return { level: 'moderate', label: 'Moderate' };
    return { level: 'mild', label: 'Mild' };
  }

  const handlePointerDown = (e) => {
    isDraggingRef.current = true;
    dragStartYRef.current = e.clientY;
    scrollStartRef.current = conjunctionListRef.current.scrollTop;

    conjunctionListRef.current.setPointerCapture(e.pointerId);
    conjunctionListRef.current.style.cursor = "grabbing";
    conjunctionListRef.current.style.userSelect = "none";
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;

    const deltaY = e.clientY - dragStartYRef.current;
    conjunctionListRef.current.scrollTop = scrollStartRef.current - deltaY;
  };

  const handlePointerUp = (e) => {
    isDraggingRef.current = false;
    conjunctionListRef.current.releasePointerCapture(e.pointerId);

    conjunctionListRef.current.style.cursor = "grab";
    conjunctionListRef.current.style.userSelect = "auto";
  };



  if (loading) return <p>Loading satellite data...</p>;
  return (
    <div ref={wrapperRef}>
      <div className="satellite-filter-wrapper">
        <div className="satellite-filter-quick-menu-container">
            <button
              className={`satellite-filter-quick-menu-toggle ${!collapsed ? 'open' : ''}`}
              onClick={toggleCollapse}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="white"
              >
                <path d="M3 4h18v2H3V4zm4 6h10v2H7v-2zm2 6h6v2H9v-2z"/>
              </svg>
            </button>
          </div>
        <div className={`satellite-filter-container ${collapsed ? 'hidden' : ''}`}>
        {/* <div className="satellite-filter-container"> */}
          {/* <h2>Satellite Filter</h2> */}
          <h2 className="panel-title">
            {/* S<span className="small-text">atellite</span> F<span className="small-text">ilter</span> */}
            Satellite Filter
          </h2>
          <div className="checkbox-group">
            {GROUPED_SATELLITE_CATEGORIES.map(({ group, items }) => (
              <fieldset key={group} className="checkbox-group-section">
                <legend className='group-title'>{group}</legend>
                {items.map(({ label, value }) => {
                  const color = CATEGORY_COLOR_MAP[value.toLowerCase()] || '#999';
                  return (
                    <label key={value} className="checkbox-label" style={{ '--checkbox-color': color }}>
                      <input
                        type="checkbox"
                        value={value}
                        checked={selectedCategories.includes(value)}
                        onChange={() => handleCheckboxChange(value)}
                      />
                      <span className="custom-checkbox" />
                      {label}
                    </label>
                  );
                })}
              </fieldset>
            ))}
          </div>
          <div className="checkbox-group">
            <fieldset className="checkbox-group-section">
              <legend className='group-title'>Orbit Type (Show Selected)</legend>
              {ORBIT_OPTIONS.map(({ label, value }) => (
                <label key={value} className="checkbox-label" style={{ '--checkbox-color': '#7093bb' }}>
                  <input
                    type="checkbox"
                    value={value}
                    checked={selectedOrbits.includes(value)}
                    onChange={() => handleOrbitChange(value)}
                  />
                  <span className="custom-checkbox" />
                  {label}
                </label>
              ))}
            </fieldset>
          </div>
          {/* <div className="drag-handle-filter" onClick={toggleCollapse}>
            <div className="drag-indicator-filter" />
          </div> */}
        </div>        
      </div>
      {isTrackingSatellite && (
        <div className='satellite-data'>
          <p>Satellite count: {results.length}</p>
          {clusterCount > 0 && (
            <p>Satellite cluster count: {clusterCount}</p>
          )}
          <br></br>
          <p>{satelliteName}</p>
          <p>{satelliteCountry}</p>
          <p>{launchDate}</p>
          <br></br>
          {conjunctions.length > 0 && (
            <div className="conjunction-panel">
              <div className="conjunction-header">
                <span className="conjunction-icon">⚠ </span>
                <span className="conjunction-title">
                  {conjunctions.length} Conjunction Event{conjunctions.length > 1 ? 's' : ''}
                </span>
              </div>

              <div
                className="conjunction-list"
                  ref={conjunctionListRef}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
              >
                {conjunctions
                  .slice()
                  .sort((a, b) => new Date(a.tca) - new Date(b.tca))
                  .map((e) => {
                    const severity = getSeverity(e.pc, e.minRangeM);

                    return (
                      <div key={e.cdmId} className={`conjunction-card severity-${severity.level}`}>
                        <div className="conjunction-card-top">
                          <span className="conjunction-object">{e.otherSatelliteName}</span>
                          <span className="conjunction-type">{e.otherObjectType}</span>
                        </div>

                        <div className="conjunction-stats">
                          <div className="conjunction-stat">
                            <span className="stat-label">TCA: </span>
                            <span className="stat-value">
                              {new Date(e.tca).toLocaleString(undefined, {
                                month: 'short', day: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <div className="conjunction-stat">
                            <span className="stat-label">Miss Dist: </span>
                            <span className="stat-value">
                              {e.minRangeM != null ? `${e.minRangeM.toLocaleString()}m` : '—'}
                            </span>
                          </div>
                          <div className="conjunction-stat">
                            <span className="stat-label">Percent: </span>
                            <span className={`stat-value severity-text-${severity.level}`}>
                              {e.pc != null ? `${(e.pc * 100).toFixed(4)}%` : 'Not published'}
                            </span>
                          </div>
                        </div>

                        <div className={`severity-badge severity-badge-${severity.level}`}>
                          {severity.label}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

